import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getTruckRoute, getMultiStopRoute } from "@/lib/mapbox";

/**
 * Returns the load's total trip mileage.
 *
 * - Multi-stop loads (3+ stops with coordinates): the SUMMED route distance
 *   across every stop in order (origin → each extra pick → drop(s)).
 * - Simple loads: origin → destination.
 *
 * The computed value is best-effort written back to `direct_loads.total_miles`
 * with `total_miles_source` marking whether it is a plain origin→destination
 * figure ('od') or a true full-route figure ('multistop'), so multi-stop loads
 * that were stored with an origin→destination estimate get upgraded once.
 *
 * Cached per (loadId + coord fingerprint) for 24h.
 */
export interface RouteStopCoord {
  stop_number?: number | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

interface Args {
  loadId: string | undefined;
  storedTotalMiles: number | null | undefined;
  originLat: number | null | undefined;
  originLng: number | null | undefined;
  destLat: number | null | undefined;
  destLng: number | null | undefined;
  /** Ordered (or unordered — sorted internally) stops for multi-stop totals. */
  stops?: RouteStopCoord[];
  /** `direct_loads.total_miles_source` when known. */
  storedMilesSource?: string | null;
}

function num(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function useResolvedLoadMiles({
  loadId,
  storedTotalMiles,
  originLat,
  originLng,
  destLat,
  destLng,
  stops,
  storedMilesSource,
}: Args): { miles: number | null; loading: boolean } {
  const waypoints: [number, number][] = (stops ?? [])
    .slice()
    .sort((a, b) => (a.stop_number ?? 0) - (b.stop_number ?? 0))
    .map((s) => [num(s.longitude), num(s.latitude)] as [number | null, number | null])
    .filter((p): p is [number, number] => p[0] !== null && p[1] !== null);

  const isMultiStop = waypoints.length > 2;
  const hasCoords = !!originLat && !!originLng && !!destLat && !!destLng;

  const hasStored = !!storedTotalMiles && storedTotalMiles > 0;
  // Multi-stop loads whose stored figure is only an origin→destination estimate
  // must be recomputed once across all legs.
  const needsMultiStopUpgrade = isMultiStop && storedMilesSource !== "multistop";

  const needsCompute =
    !!loadId && (isMultiStop ? needsMultiStopUpgrade : hasCoords && !hasStored);

  const fp = isMultiStop
    ? `${loadId}|multi|${waypoints.map((w) => w.join(",")).join(";")}`
    : `${loadId}|${originLat}|${originLng}|${destLat}|${destLng}`;

  const q = useQuery({
    queryKey: ["resolved-load-miles", fp],
    enabled: needsCompute,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const r = isMultiStop
        ? await getMultiStopRoute(waypoints)
        : await getTruckRoute(
            [originLng as number, originLat as number],
            [destLng as number, destLat as number],
          );
      const miles = r?.distance ? Math.round(r.distance) : null;
      // Best-effort DB backfill so bid cards, Atlas, analytics all see it.
      if (miles && loadId) {
        supabase
          .from("direct_loads" as any)
          .update({ total_miles: miles, total_miles_source: isMultiStop ? "multistop" : "od" })
          .eq("id", loadId)
          .then(({ error }) => {
            if (error) console.warn("[useResolvedLoadMiles] backfill failed", error.message);
          });
      }
      return miles;
    },
  });

  if (needsCompute) {
    // While a multi-stop upgrade is in flight, keep showing the stored estimate
    // instead of an empty dash.
    const computed = q.data ?? null;
    if (computed) return { miles: computed, loading: false };
    return {
      miles: hasStored ? (storedTotalMiles as number) : null,
      loading: q.isLoading || q.isFetching,
    };
  }
  if (hasStored) return { miles: storedTotalMiles as number, loading: false };
  if (!hasCoords) return { miles: null, loading: false };
  return { miles: q.data ?? null, loading: q.isLoading || q.isFetching };
}
